import { useQuery } from "@tanstack/react-query";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { getLeadhoopStats } from "@/api/leadhoop";
import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { Spinner } from "@/components/Spinner";
import { fmtNumber } from "@/lib/format";

const COLORS = [
  "#3b82f6",
  "#22c55e",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#14b8a6",
];

export function LeadhoopDashboard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["leadhoop", "stats"],
    queryFn: getLeadhoopStats,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="LeadHoop dashboard"
        subtitle="Overview of leads synced from LeadHoop"
      />

      {error && <ErrorBanner error={error} />}

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Spinner /> Loading stats…
        </div>
      )}

      {data && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="card p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Total leads
              </div>
              <div className="mt-1 text-3xl font-semibold tabular-nums">
                {fmtNumber(data.count_total)}
              </div>
            </div>
            <div className="card p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Accounts configured
              </div>
              <div className="mt-1 text-3xl font-semibold tabular-nums">
                {data.count_by_account.length}
              </div>
            </div>
            <div className="card p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Distinct statuses
              </div>
              <div className="mt-1 text-3xl font-semibold tabular-nums">
                {data.count_by_status.length}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="card p-4">
              <h3 className="mb-3 text-sm font-semibold text-slate-700">
                Leads per day (last 30 days)
              </h3>
              <div className="h-64">
                <ResponsiveContainer>
                  <AreaChart data={data.count_by_day}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#e2e8f0"
                    />
                    <XAxis dataKey="day" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke="#3b82f6"
                      fill="#3b82f6"
                      fillOpacity={0.2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="card p-4">
              <h3 className="mb-3 text-sm font-semibold text-slate-700">
                Status breakdown
              </h3>
              <div className="h-64">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={data.count_by_status}
                      dataKey="count"
                      nameKey="label"
                      outerRadius={90}
                      label
                    >
                      {data.count_by_status.map((_, i) => (
                        <Cell
                          key={i}
                          fill={COLORS[i % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">
              By account
            </h3>
            <table className="table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th className="text-right">Leads</th>
                </tr>
              </thead>
              <tbody>
                {data.count_by_account.map((e) => (
                  <tr key={e.label}>
                    <td>{e.label}</td>
                    <td className="text-right tabular-nums">
                      {fmtNumber(e.count)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
